import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { withAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import Homepage from './components/Homepage';
import "./App.css";
import AppHeader from './components/AppHeader';
import PlatformShell from './platform/shell/PlatformShell';
import FlowSwitcher from './platform/shell/FlowSwitcher';
import useOperationalStore from './platform/store/useOperationalStore';
import { hasFeature, hasRoadService } from './platform/engine/entitlements';
import { ensureCustomerAndUnit } from './platform/engine/identitySpine';


// Assets
import logo from './Assets/Just Rekon.png';

// Configuration and Utilities
import * as mutations from './graphql/mutations';
import { DEFAULT_LOCATION_ID } from './platform/events/operationEvents';

// Desktop Components
import ROListView from './components/ROListView';
import ShopMapView from './views/ShopMapView';
import CommandView from './views/CommandView';
import TimelineView from './views/TimelineView';
import PriorityView from './views/PriorityView';
import FlowView from './views/FlowView';
import HistoryView from './views/HistoryView';
import RoadServiceView from './views/RoadServiceView';
import RoadLocationPingPage from './views/RoadLocationPingPage';
import EstimateApprovePage from './views/EstimateApprovePage';
import TechnicianModal from './components/Modals/TechnicianModal';
import TechnicianView from './components/TechnicianView';
import NewROModal from './components/Modals/NewROModal';
import CustomerDashboard from './components/CustomerDashboard';
import CustomerDetailPage from './components/CustomerDetailPage';
import CustomizeView from './views/CustomizeView';
import ROPage from './views/ROPage';
import CalendarView from './views/CalendarView';
import BillingView from './views/BillingView';
import UsersView from './views/UsersView';
import MoneyView from './views/MoneyView';
import MarketingHome from './components/MarketingHome';
import PrivacyPage from './views/PrivacyPage';
import useShopSettings from './platform/settings/useShopSettings';

// Mobile Components
import MobileHeader from './components/Mobile/MobileHeader';
import MobileKanbanBoard from './components/Mobile/MobileKanbanBoard';
import MobileJobsList from './components/Mobile/MobileJobsList';
import MobileLauncher from './components/Mobile/MobileLauncher';
import RODetailPanel from './platform/shell/RODetailPanel';



const client = generateClient();

// Replace your existing DesktopHeader component in App.js with this updated version:

const DesktopHeader = ({ setIsAddModalOpen, setIsTechModalOpen, setIsRedBookOpen, navigate, customerCheckIns }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const menuButtonStyle = {
    width: "100%",
    padding: "10px 14px",
    background: "white",
    border: "1px solid #ccc",
    borderRadius: "2px",
    cursor: "pointer",
    textAlign: "left",
    fontSize: "14px"
  };

  return (
    <div
      className="app-header"
      style={{
        display: "flex",
        alignItems: "center",
        marginBottom: "20px",
        position: "sticky",
        top: 0,
        zIndex: 100,
        justifyContent: "space-between",
        background: "#ffffff",
        borderBottom: "1px solid #e2e8f0",
        padding: "10px 0"
      }}
    >
      {/* Home icon button (left) */}
      <button
        onClick={() => navigate("/home")}
        title="Home"
        style={{
          marginLeft: "20px",
          background: "transparent",
          padding: "8px 12px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#334155"
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
          <polyline points="9 21 9 12 15 12 15 21" />
        </svg>
      </button>

      {/* Center Logo — overlaps below header */}
      <img
        src={logo}
        alt="REKON Operational Intelligence"
        style={{
          height: "250px",
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: "-100px",
          zIndex: 10,
          filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.25))"
        }}
      />
        

      {/* Right side: Hamburger */}
      <div style={{ position: "relative", marginRight: "20px" }}>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Open menu"
          aria-expanded={menuOpen ? "true" : "false"}
          style={{
            background: "transparent",
          color: "#334155",
            border: "none",
            padding: "10px 12px",
            borderRadius: "2px",
            cursor: "pointer",
            fontSize: "18px",
            lineHeight: "18px"
          }}
        >
          ☰
          {customerCheckIns.length > 0 && (
            <span
              style={{
                marginLeft: "10px",
                background: "#F59E0B",
                padding: "2px 8px",
                borderRadius: "999px",
                fontSize: "12px"
              }}
            >
              {customerCheckIns.length}
            </span>
          )}
        </button>

        {/* Dropdown menu */}
        {menuOpen && (
          <>
            {/* Click-outside backdrop */}
            <div
              onClick={closeMenu}
              style={{
                position: "fixed",
                inset: 0,
                background: "transparent",
                zIndex: 999
              }}
            />

            <div
              style={{
                position: "absolute",
                right: 0,
                top: "48px",
                width: "260px",
                background: "white",
                border: "1px solid #ddd",
                borderRadius: "2px",
                padding: "12px",
                zIndex: 1000,
                boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                display: "flex",
                flexDirection: "column",
                gap: "10px"
              }}
            >
              <button
                style={menuButtonStyle}
                onClick={() => {
                  setIsTechModalOpen(true);
                  closeMenu();
                }}
              >
                Manage Team
              </button>

              <button
                style={{
                  ...menuButtonStyle,
                  borderColor: "#F59E0B",
                  fontWeight: "bold"
                }}
                onClick={() => {
                  setIsAddModalOpen(true);
                  closeMenu();
                }}
              >
                Add Service Unit{" "}
                {customerCheckIns.length > 0 ? `(${customerCheckIns.length})` : ""}
              </button>

              <button
                style={{ ...menuButtonStyle, borderColor: "#F59E0B" }}
                onClick={() => {
                  setIsRedBookOpen(true);
                  closeMenu();
                }}
              >
                Scheduling
              </button>

              <button
                style={{ ...menuButtonStyle, borderColor: "#FF8C00" }}
                onClick={() => {
                  navigate("/customers");
                  closeMenu();
                }}
              >
                Accounts
              </button>

              <button
                style={{ ...menuButtonStyle, borderColor: "#4CAF50" }}
                onClick={() => {
                  navigate("/analytics");
                  closeMenu();
                }}
              >
                Intelligence
              </button>

              <button
                style={{ ...menuButtonStyle, borderColor: "#0066CC" }}
                onClick={() => {
                  navigate("/tech-view");
                  closeMenu();
                }}
              >
                Team View
              </button>

              <button
                style={{ ...menuButtonStyle, borderColor: "#6366f1" }}
                onClick={() => {
                  navigate("/customize");
                  closeMenu();
                }}
              >
                Customize
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};



function MobileShell({ isAddModalOpen, setIsAddModalOpen, isTechModalOpen, setIsTechModalOpen, handleMobileAddTruck }) {
  const { pathname } = useLocation();
  const isLauncher   = pathname === '/home';

  return (
    <div style={{ minHeight: '100vh', background: isLauncher ? '#0f172a' : '#f8fafc' }}>
      {!isLauncher && (
        <MobileHeader onAddClick={() => setIsAddModalOpen(true)} />
      )}
      <div style={{ paddingTop: isLauncher ? 0 : 52 }}>
        <Routes>
          <Route path="/home" element={
            <MobileLauncher
              onAddTruck={() => setIsAddModalOpen(true)}
              onManageTeam={() => setIsTechModalOpen(true)}
            />
          } />
          <Route path="/flow"      element={<MobileKanbanBoard />} />
          <Route path="/ro/:id"    element={<ROPage />} />
          <Route path="/ro-list"   element={<MobileJobsList />} />
          <Route path="/timeline"  element={<div style={{ padding: 12 }}><TimelineView /></div>} />
          <Route path="/command"   element={<CommandView />} />
          <Route path="/road-service" element={
            <div style={{ height: 'calc(100vh - 52px)', display: 'flex', flexDirection: 'column' }}>
              <RoadServiceView />
            </div>
          } />
          <Route path="/priority" element={
            <div style={{ height: 'calc(100vh - 52px)', display: 'flex', flexDirection: 'column' }}>
              <PriorityView />
            </div>
          } />
          <Route path="/schedule"  element={<ScheduleRouteView />} />
          <Route path="/customers" element={<CustomerDashboard />} />
          <Route path="/customers/:companyName" element={<CustomerDetailPage />} />
          {/* Any path mobile doesn't serve lands on the launcher, never a blank page */}
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </div>
      <RODetailPanel />
      {isAddModalOpen && (
        <NewROModal
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={handleMobileAddTruck}
          customerCheckIns={[]}
          onCustomerProcessed={() => {}}
        />
      )}
      {isTechModalOpen && (
        <TechnicianModal onClose={() => setIsTechModalOpen(false)} />
      )}
    </div>
  );
}

function App() {
  const [isMobileView, setIsMobileView] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isTechModalOpen, setIsTechModalOpen] = useState(false);
  const fetchAll = useOperationalStore(s => s.fetchAll);
  const localAddTruck = useOperationalStore(s => s.localAddTruck);
  const startSubscriptions = useOperationalStore(s => s.startSubscriptions);
  const stopSubscriptions = useOperationalStore(s => s.stopSubscriptions);
  const loadSettings = useShopSettings(s => s.load);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Sync role from the signed-in user's Cognito group. Users created from the
  // Users page get a role group; that group becomes their rekon_role so all
  // permission checks reflect who actually signed in. Users without a group
  // keep the existing localStorage behavior.
  useEffect(() => {
    fetchAuthSession()
      .then(({ tokens }) => {
        const groups = tokens?.accessToken?.payload?.['cognito:groups'] || [];
        if (groups.length) window.localStorage.setItem('rekon_role', groups[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobileView) return undefined;

    fetchAll();
    startSubscriptions();
    const refreshQuietly = () => fetchAll({ quiet: true });
    const interval = setInterval(refreshQuietly, 5000);
    window.addEventListener('focus', refreshQuietly);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refreshQuietly);
      stopSubscriptions();
    };
  }, [isMobileView, fetchAll, startSubscriptions, stopSubscriptions]);

  const handleMobileAddTruck = async (truckData) => {
    const currentTime = Math.floor(Date.now() / 1000);

    try {
      // Identity spine dual-write: link the job to its Customer/Unit records.
      // Non-fatal — a spine failure still creates the job (legacy strings).
      const spine = await ensureCustomerAndUnit(truckData);
      const result = await client.graphql({
        query: mutations.createTruck,
        variables: {
          input: {
            repairOrder: truckData.repairOrder,
            locationId: DEFAULT_LOCATION_ID,
            shopId: spine.shopId,
            customerId: spine.customerId,
            unitId: spine.unitId,
            companyName: truckData.companyName,
            truckNumber: truckData.truckNumber || '',
            reason: truckData.reason || null,
            contactPerson: truckData.contactPerson || '',
            phone: truckData.phone || '',
            email: truckData.email || '',
            partsHandling: truckData.partsHandling || '',
            customerCheckInId: truckData.customerCheckInId || null,
            year: truckData.year || '',
            make: truckData.make || '',
            model: truckData.model || '',
            vin: truckData.vin || '',
            mileage: truckData.mileage || '',
            hours: truckData.hours || '',
            faults: truckData.faults || '',
            customerType: truckData.customerType || null,
            poNumber: truckData.poNumber || null,
            status: 'yard',
            position: 0,
            warranty: truckData.warranty || false,
            startTime: currentTime,
            lastStatusChange: currentTime,
            lastNote: currentTime,
            timeInWip: 0,
            wipStartTime: null,
            isPaused: false,
            notes: [],
            statusHistory: [{
              status: 'yard',
              timeEntered: currentTime
            }],
            estimatedTime: 0,
            expired: false,
            warning: false,
            critical: false
          }
        }
      });
      setIsAddModalOpen(false);
      // Same anti-vanish treatment as desktop: put the new truck in the
      // store directly instead of racing an eventually-consistent refetch.
      const newTruck = result?.data?.createTruck;
      if (newTruck?.id) localAddTruck(newTruck);
      else fetchAll({ quiet: true });
    } catch (error) {
      console.error('Error adding truck from mobile web:', error);
      alert(`Could not create the RO: ${error?.errors?.[0]?.message || error.message || 'unknown error'}. Please try again.`);
    }
  };

  if (isMobileView) {
    return (
      <MobileShell
        isAddModalOpen={isAddModalOpen}
        setIsAddModalOpen={setIsAddModalOpen}
        isTechModalOpen={isTechModalOpen}
        setIsTechModalOpen={setIsTechModalOpen}
        handleMobileAddTruck={handleMobileAddTruck}
      />
    );
  }

  return <DesktopContent />;
}


// Permanent comp/exempt bypass — these shops get full access regardless of Stripe state.
// DGP-ALBANY is the live pilot shop and must never be gated.
const BILLING_EXEMPT_SHOPS = new Set(['DGP-ALBANY']);

// settings = full shop settings object from useShopSettings
function canAccess(settings, feature) {
  if (settings?.billingExempt) return true;
  if (BILLING_EXEMPT_SHOPS.has(settings?.business?.shopId)) return true;
  return hasFeature(settings, feature);
}

// Views that need a light padded wrapper instead of the dark shell background
const LIGHT_VIEWS = ['timeline', 'command'];
const lightWrap = (children) => (
  <div style={{ flex: 1, overflow: 'auto', background: '#f1f5f9', padding: 16 }}>
    {children}
  </div>
);

function FlowViewRouter({
  isAddModalOpen,
  setIsAddModalOpen,
  customerCheckIns,
  setCustomerCheckIns,
  handleAddTruck,
  setBoardState,
}) {
  const activeView = useOperationalStore(s => s.activeView);
  const setActiveView = useOperationalStore(s => s.setActiveView);
  const location = useLocation();

  // ?view= is the shareable address of a lens — redirected old routes
  // (/home, /tech-view) and deep links land here after mount, so sync on
  // every URL change, not just PlatformShell's one-time mount read.
  useEffect(() => {
    const view = new URLSearchParams(location.search).get('view');
    if (view && view !== activeView) setActiveView(view);
  }, [location.search]);

  const sharedProps = {
    isAddModalOpen,
    setIsAddModalOpen,
    onCustomerCheckInsUpdate: setCustomerCheckIns,
    // Opens the New RO modal. The modal's onSubmit (handleAddTruck) does the
    // actual create — views must never receive the submit handler as a button
    // onClick, or clicking silently passes a mouse event as the truck data.
    onAddTruck: () => setIsAddModalOpen(true),
    onBoardStateChange: setBoardState,
  };

  function renderView() {
    switch (activeView) {
      case 'today':
        return (
          <div style={{ flex: 1, overflow: 'auto', background: '#f8fafc' }}>
            <Homepage />
          </div>
        );
      case 'timeline':
        return lightWrap(<TimelineView />);
      case 'shop-map':
        return (
          <div style={{ flex: 1, overflow: 'auto', background: '#f1f5f9', padding: 16 }}>
            <ShopMapView />
          </div>
        );
      case 'command':
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc', padding: 16 }}>
            <CommandView />
          </div>
        );
      case 'priority':
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>
            <PriorityView />
          </div>
        );
      case 'history':
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>
          <HistoryView />
          </div>
        );
      case 'road-service':
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>
            <RoadServiceView />
          </div>
        );
      case 'flow':
        return <FlowView onAddTruck={() => setIsAddModalOpen(true)} />;
      case 'tech-view':
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f0f0f0' }}>
            <TechnicianView />
          </div>
        );
      case 'ro-list':
      default:
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>
            <ROListView {...sharedProps} />
          </div>
        );
    }
  }

  // The Board lens renders its own switcher merged into its toolbar
  // (fv-toolbar); every other lens has no toolbar of its own, so the
  // switcher stands alone above it here.
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
      {activeView !== 'flow' && <FlowSwitcher />}
      {renderView()}
    </div>
  );
}

function ScheduleRouteView() {
  return <CalendarView />;
}

const DesktopContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const setActiveView = useOperationalStore(s => s.setActiveView);
  const fetchAll = useOperationalStore(s => s.fetchAll);
  const localAddTruck = useOperationalStore(s => s.localAddTruck);
  const shopSettings = useShopSettings(s => s.settings);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isTechModalOpen, setIsTechModalOpen] = useState(false);
  const [customerCheckIns, setCustomerCheckIns] = useState([]);
  const [, setBoardState] = useState({});



const handleCustomerProcessed = async (customerCheckInId) => {
  setCustomerCheckIns(prev => prev.filter(c => c.id !== customerCheckInId));
  try {
    await client.graphql({
      query: mutations.updateCustomerCheckIn,
      variables: { input: { id: customerCheckInId, status: 'processed' } }
    });
  } catch (error) {
    console.error('Failed to mark check-in processed:', error.message);
  }
};

  


const handleAddTruck = async (truckData) => {
  const currentTime = Math.floor(Date.now() / 1000);

  const activities = [];
  if (truckData.initialNote?.trim()) {
    activities.push({
      id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: 'note',
      text: truckData.initialNote.trim(),
      author: null,
      timestamp: currentTime,
      photos: [],
      customerFacing: false,
      category: null,
      stage: 'intake',
    });
  }

  const techNumbers = truckData.techNumber ? [String(truckData.techNumber)] : [];

  try {
    // Identity spine dual-write: link the job to its Customer/Unit records.
    // Non-fatal — a spine failure still creates the job (legacy strings).
    const spine = await ensureCustomerAndUnit(truckData);
    const result = await client.graphql({
      query: mutations.createTruck,
      variables: {
        input: {
          repairOrder: truckData.repairOrder,
          locationId: DEFAULT_LOCATION_ID,
          shopId: spine.shopId,
          customerId: spine.customerId,
          unitId: spine.unitId,
          companyName: truckData.companyName,
          truckNumber: truckData.truckNumber || '',
          reason: truckData.reason || null,
          contactPerson: truckData.contactPerson || '',
          phone: truckData.phone || '',
          email: truckData.email || '',
          partsHandling: truckData.partsHandling || '',
          customerCheckInId: truckData.customerCheckInId || null,
          year: truckData.year || '',
          make: truckData.make || '',
          model: truckData.model || '',
          vin: truckData.vin || '',
          mileage: truckData.mileage || '',
          hours: truckData.hours || '',
          faults: truckData.faults || '',
          customerType: truckData.customerType || null,
          poNumber: truckData.poNumber || null,
          status: 'yard',
          position: 0,
          warranty: truckData.warranty || false,
          promisedTime: truckData.promisedTime || null,
          techNumbers: techNumbers.length ? techNumbers : null,
          activities: activities.length ? activities : null,
          startTime: currentTime,
          lastStatusChange: currentTime,
          lastNote: currentTime,
          timeInWip: 0,
          wipStartTime: null,
          isPaused: false,
          notes: [],
          statusHistory: [{ status: 'yard', timeEntered: currentTime }],
          estimatedTime: 0,
          expired: false,
          warning: false,
          critical: false,
        }
      }
    });

    const newTruck = result?.data?.createTruck;

    if (truckData.customerCheckInId) {
      await handleCustomerProcessed(truckData.customerCheckInId);
      setCustomerCheckIns(prev => prev.filter(c => c.id !== truckData.customerCheckInId));
    }

    setIsAddModalOpen(false);
    // Put the new truck in the store directly — a refetch this soon after
    // create can miss it (eventually consistent reads) and the RO looked
    // like it never saved. No refetch needed; polling reconciles later.
    if (newTruck?.id) {
      localAddTruck(newTruck);
    } else {
      await fetchAll({ quiet: true });
    }
    setActiveView('flow');
    if (location.pathname !== '/flow') navigate('/flow');
  } catch (error) {
    console.error('Error adding truck:', error);
    alert(`Could not create the RO: ${error?.errors?.[0]?.message || error.message || 'unknown error'}. Please try again.`);
  }
};

  // One persistent shell around every authenticated route: the sidebar and
  // header never unmount, and the store's fetch/subscriptions run once —
  // navigating between pages no longer tears down and rebuilds the frame.
  // Pages that don't manage their own viewport scroll get a .shell-page
  // scroll container.
  const page = (el) => <div className="shell-page">{el}</div>;

  return (
    <div style={{ height: '100vh', overflow: 'hidden', background: '#f8fafc' }}>
      <PlatformShell
        customerCheckIns={customerCheckIns}
        onManageTechs={() => setIsTechModalOpen(true)}
        onAddTruck={() => setIsAddModalOpen(true)}
      >
        <Routes>
          {/* The board is home; Today is a Flow lens */}
          <Route path="/" element={<Navigate to="/flow" replace />} />
          <Route path="/home" element={<Navigate to="/flow?view=today" replace />} />
          <Route path="/flow" element={
            <FlowViewRouter
              isAddModalOpen={isAddModalOpen}
              setIsAddModalOpen={setIsAddModalOpen}
              customerCheckIns={customerCheckIns}
              setCustomerCheckIns={setCustomerCheckIns}
              handleAddTruck={handleAddTruck}
              setBoardState={setBoardState}
            />
          } />
          <Route path="/schedule" element={<ScheduleRouteView />} />
          <Route path="/road" element={
            hasRoadService(shopSettings) ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>
                <RoadServiceView />
              </div>
            ) : page(<BillingView />)
          } />
          <Route path="/money" element={page(
            <MoneyView canViewReports={canAccess(shopSettings, 'analytics')} />
          )} />
          <Route path="/customers" element={page(
            canAccess(shopSettings, 'customers') ? <CustomerDashboard /> : <BillingView />
          )} />
          <Route path="/customers/:companyName" element={page(
            canAccess(shopSettings, 'customers') ? <CustomerDetailPage /> : <BillingView />
          )} />
          <Route path="/customize" element={page(<CustomizeView onBack={() => navigate(-1)} />)} />
          <Route path="/users" element={page(<UsersView />)} />
          <Route path="/ro/:id" element={page(<ROPage />)} />
          <Route path="/billing" element={page(<BillingView />)} />

          {/* Old destinations — every pre-Phase-2 URL keeps working */}
          <Route path="/collections" element={<Navigate to="/money?tab=unpaid" replace />} />
          <Route path="/analytics" element={<Navigate to="/money?tab=analytics" replace />} />
          <Route path="/reports/cycle-time" element={<Navigate to="/money?tab=cycle-time" replace />} />
          <Route path="/tech-view" element={<Navigate to="/flow?view=tech-view" replace />} />
        </Routes>
      </PlatformShell>

      {isAddModalOpen && (
        <NewROModal
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={handleAddTruck}
          customerCheckIns={customerCheckIns}
          onCustomerProcessed={(id) => setCustomerCheckIns(prev => prev.filter(c => c.id !== id))}
        />
      )}

      {isTechModalOpen && (
        <TechnicianModal
          onClose={() => setIsTechModalOpen(false)}
          isMobile={false}
        />
      )}
    </div>
  );
};

// hideSignUp: staff accounts are created from the Users page (or AWS console),
// not by open self-registration against shop data.
const AuthenticatedApp = withAuthenticator(App, { hideSignUp: true });

function RootApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;

  // After an interactive sign-in, land on the board — it's home. Session
  // restores on page reload don't emit 'signedIn', so deep links still work.
  useEffect(() => {
    return Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') navigate('/flow');
    });
  }, [navigate]);

  if (pathname === '/') return <MarketingHome />;
  if (pathname === '/privacy') return <PrivacyPage />;
  if (pathname === '/billing') return <BillingView />;

  if (pathname.startsWith('/road-location/') || pathname.startsWith('/estimate/')) {
    return (
      <Routes>
        <Route path="/road-location/:token" element={<RoadLocationPingPage />} />
        <Route path="/estimate/:token" element={<EstimateApprovePage />} />
      </Routes>
    );
  }
  return <AuthenticatedApp />;
}

export default RootApp;
